/**
 * Protected route component for admin-only pages
 * Requires Clerk authentication and admin role
 */

import { useIsAdmin } from "@/lib/auth";
import { getClerkPublishableKey } from "@/lib/clerkPublishableKey";
import { RedirectToSignIn, useAuth } from "@clerk/clerk-react";
import { Navigate } from "react-router-dom";

type ProtectedAdminRouteProps = {
  children: React.ReactNode;
  title?: string; // Optional title for backwards compatibility
};

/**
 * Protects admin routes - requires Clerk authentication and admin role
 */
export function ProtectedAdminRoute({ children }: ProtectedAdminRouteProps) {
  if (!getClerkPublishableKey()) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background p-6 text-center">
        <p className="text-lg font-semibold text-foreground">Admin: Clerk no configurado en el build</p>
        <p className="text-sm text-muted-foreground max-w-md">
          Define <code className="rounded bg-muted px-1">VITE_CLERK_PUBLISHABLE_KEY</code> o{" "}
          <code className="rounded bg-muted px-1">NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY</code> y vuelve a
          desplegar.
        </p>
      </div>
    );
  }

  const { isLoaded, isSignedIn } = useAuth();
  const isAdmin = useIsAdmin();

  // Show loading state while Clerk is initializing
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  // Redirect to sign-in if not authenticated
  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  // Redirect to home if not admin
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Default export for backwards compatibility
export default ProtectedAdminRoute;
